import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  AppDialogRef,
  AppDialogService,
  AuthService,
  ErrorComponent,
  InputWithErrorComponent,
  Organization,
  OrganizationService,
  SaveableFormComponent,
  User,
  UserRole,
  UserService,
  derivePrimaryRole,
  getAssignableUserRolesForEditor,
  normalizeUserRoles,
} from '@nimmsta/ng-commons-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MultiSelect } from 'primeng/multiselect';

@Component({
  selector: 'app-user-form',
  templateUrl: 'user-form.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
    MultiSelect,
  ],
})
export class UserFormComponent
  extends SaveableFormComponent<User>
  implements OnInit
{
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private modalService = inject(AppDialogService);
  private orgService = inject(OrganizationService);
  private translate = inject(TranslateService);

  protected override dataService = inject(UserService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  @Input() allUserTable = false;

  @ViewChild('resetModal', { static: true })
  resetModal!: TemplateRef<unknown>;

  assignableRoles = this.createRoleOptions([UserRole.MANAGER]);

  readonly MAX_SECONDARY_ORGANIZATIONS = 40;

  allOrgs: Organization[] = [];

  orgs: Organization[] = [];

  secondaryOrgs: string[] = [];

  oldSecondaryOrgs: string[] = [];

  modalRef?: AppDialogRef;

  constructor() {
    super();
    this.setValue({} as User);
  }

  ngOnInit(): void {
    if (this.allUserTable) {
      this.orgService.list({ pageSize: 9999 }).subscribe((result) => {
        this.allOrgs = result.content;
      });
    }
  }

  override setValue(value: User): void {
    super.setValue(value);
    if (value.id) {
      this.form.controls['email'].disable();
    } else {
      this.form.controls['email'].enable();
    }
    this.authService.getCurrentUser().subscribe((user) => {
      const assignableRoles = this.getAssignableRoles(user.role);
      this.assignableRoles = this.createRoleOptions(assignableRoles);
    });
    if (this.allUserTable) {
      this.orgs = this.allOrgs.filter(
        (org) => org.id !== value.organization.id,
      );
      this.secondaryOrgs = value.secondaryOrganizations
        ? value.secondaryOrganizations.map((org) => org.id)
        : [];
      this.limitSecondaryOrganizations(this.secondaryOrgs);
      this.oldSecondaryOrgs = value.secondaryOrganizations
        ? value.secondaryOrganizations.map((org) => org.id)
        : [];
    }
  }

  static passwordRepeat(form: AbstractControl): ValidationErrors | null {
    const passwordControl = form.get('password');
    const passwordRepeatControl = form.get('repeat_password');
    const message = {
      not_matching: '',
    };
    if (
      passwordControl?.value !== passwordRepeatControl?.value &&
      (form.value.id === 0 || passwordControl?.value !== '')
    ) {
      passwordRepeatControl?.setErrors(message);
    }
    return null;
  }

  protected setFormGroup(): void {
    const visibleRoles = this.getVisibleRoles(
      this.value.roles ?? [this.value.role],
    );

    this.form = this.fb.group({
      id: this.value.id,
      email: [this.value.email, [Validators.required, Validators.email]],
      given_name: [
        this.value.given_name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(30),
        ],
      ],
      family_name: [
        this.value.family_name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(30),
        ],
      ],
      roles: [visibleRoles],
    });
    this.form.setValidators(UserFormComponent.passwordRepeat);
  }

  protected getValidators(): ValidatorFn[] {
    if (this.value.id === '') {
      return [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(25),
      ];
    }
    return [];
  }

  resetPasswordPrompt(): void {
    this.modalRef = this.modalService.open(this.resetModal);
  }

  resetPassword(): void {
    this.modalRef?.close();
    this.dataService.resetPassword(this.value).subscribe({
      error: (response) => {
        this.error = response;
      },
    });
  }

  saveSecondaryOrgs(): void {
    this.limitSecondaryOrganizations(this.secondaryOrgs);
    this.dataService
      .saveSecondaryOrgs(this.value.id, this.secondaryOrgs)
      .subscribe({
        error: (response) => {
          this.error = response;
        },
      });
  }

  limitSecondaryOrganizations(orgIds: string[] | null): void {
    this.secondaryOrgs = orgIds ?? [];
    if (this.secondaryOrgs.length > this.MAX_SECONDARY_ORGANIZATIONS) {
      this.secondaryOrgs = this.secondaryOrgs.slice(
        0,
        this.MAX_SECONDARY_ORGANIZATIONS,
      );
    }
  }

  override onSave(): void {
    this.limitSecondaryOrganizations(this.secondaryOrgs);
    if (
      this.form.pristine &&
      this.form.valid &&
      this.haveSameStringValues(this.secondaryOrgs, this.oldSecondaryOrgs)
    ) {
      this.closeEvent.emit();
      return;
    }
    if (this.form.valid) {
      this.error = undefined;
      this.loadingEvent.emit(true);
      const originalRoles = normalizeUserRoles(
        this.value.roles ?? [this.value.role],
      );
      this.value = Object.assign(this.value, this.form.value);
      const rolesValue = this.form.getRawValue()['roles'];
      const selectedRoles = Array.isArray(rolesValue)
        ? (rolesValue as Array<UserRole | string | null | undefined>)
        : [rolesValue as UserRole | string | null | undefined];
      const normalizedSelectedRoles = normalizeUserRoles(selectedRoles);
      this.value.roles =
        this.value.id &&
        this.haveSameRoles(originalRoles, normalizedSelectedRoles)
          ? undefined
          : normalizedSelectedRoles;
      this.value.role = derivePrimaryRole(this.value.roles ?? originalRoles);
      this.form.disable();
      this.applyCustomValues();
      this.dataService.createOrUpdate(this.value).subscribe({
        next: (value) => {
          if (
            this.allUserTable &&
            !this.haveSameStringValues(
              this.secondaryOrgs,
              this.oldSecondaryOrgs,
            )
          ) {
            this.dataService
              .saveSecondaryOrgs(this.value.id, this.secondaryOrgs)
              .subscribe({
                next: () => {
                  this.value = value;
                  this.closeEvent.emit();
                  this.form.enable();
                  this.form.markAsPristine();
                  this.loadingEvent.emit(false);
                },
                error: (response) => {
                  this.error = response;
                  this.form.enable();
                  this.loadingEvent.emit(false);
                },
              });
          } else {
            this.value = value;
            this.closeEvent.emit();
            this.form.enable();
            this.form.markAsPristine();
            this.loadingEvent.emit(false);
          }
        },
        error: (response) => {
          this.error = response;
          this.form.enable();
          this.loadingEvent.emit(false);
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  private getVisibleRoles(
    roles: Array<UserRole | string | null | undefined> | null | undefined,
  ): UserRole[] {
    return normalizeUserRoles(roles).filter((role) => role !== UserRole.USER);
  }

  private getAssignableRoles(
    editorRole: UserRole | string | null | undefined,
  ): UserRole[] {
    return getAssignableUserRolesForEditor(editorRole);
  }

  private createRoleOptions(
    roles: UserRole[],
  ): Array<{ label: string; value: UserRole }> {
    return roles.map((role) => ({
      label: this.translate.instant(role),
      value: role,
    }));
  }

  private haveSameRoles(left: UserRole[], right: UserRole[]): boolean {
    return (
      left.length === right.length &&
      left.every((role, index) => role === right[index])
    );
  }

  private haveSameStringValues(left: string[], right: string[]): boolean {
    return (
      left.length === right.length &&
      left.every((value, index) => value === right[index])
    );
  }
}
